import { NgZone } from '@angular/core';
import {
  BehaviorSubject,
  ObjectUnsubscribedError,
  Observable,
  Observer,
  SubscriptionLike,
} from 'rxjs';

export class BehaviorObservable<T> extends Observable<T> implements SubscriptionLike {
  closed = false;

  private currentObservers: Observer<T>[] | null = null;

  observers: Observer<T>[] = [];
  isStopped = false;
  hasError = false;
  thrownError: any = null;

  constructor(private _value: T, private _zone: NgZone) {
    // NOTE: This must be here to obscure Observable's constructor.
    super();
  }

  /** @internal */
  protected _throwIfClosed() {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    }
  }

  next(value: T) {
    this._zone.run(() => {
      errorContext(() => {
        this._throwIfClosed();
        if (!this.isStopped) {
          if (!this.currentObservers) {
            this.currentObservers = Array.from(this.observers);
          }
          for (const observer of this.currentObservers) {
            observer.next(value);
          }
        }
      });
    });
  }

  error(err: any) {
    this._zone.run(() => {
      errorContext(() => {
        this._throwIfClosed();
        if (!this.isStopped) {
          this.hasError = this.isStopped = true;
          this.thrownError = err;
          const { observers } = this;
          while (observers.length) {
            observers.shift()!.error(err);
          }
        }
      });
    });
  }

  complete() {
    this._zone.run(() => {
      errorContext(() => {
        this._throwIfClosed();
        if (!this.isStopped) {
          this.isStopped = true;
          const { observers } = this;
          while (observers.length) {
            observers.shift()!.complete();
          }
        }
      });
    });
  }

  unsubscribe(): void {
    this.isStopped = this.closed = true;
    this.observers = [];
  }

  get observed() {
    return this.observers?.length > 0;
  }

  get value(): T {
    return this.getValue();
  }

  getValue(): T {
    const { hasError, thrownError, _value } = this;
    if (hasError) {
      throw thrownError;
    }
    this._throwIfClosed();
    return _value;
  }
}

function errorContext(cb: () => void) {
  // This is the general non-deprecated path for everyone that
  // isn't crazy enough to use super-gross mode (useDeprecatedSynchronousErrorHandling)
  cb();
}

export class LoggingBehaviorSubject<T> extends BehaviorSubject<T> {
  private prefix: string;
  public loggingEnabled: boolean;

  constructor(initialValue: T, prefix: string, loggingEnabled: boolean = true) {
    super(initialValue);
    this.prefix = prefix;
    this.loggingEnabled = loggingEnabled;
  }

  // Override the `next' method
  override next(value: T, label?: string): void {
    if (this.loggingEnabled) {
      const caller = this.getCaller();
      const callerLog = caller ? `by: ${caller}` : '';
      const logLabel = label ? `| Label: ${label}` : '';

      console.log(`${this.prefix} nextWithLog called ${callerLog} with value:`, value, logLabel);
    }
    super.next(value);
  }

  setPrefix(prefix: string): void {
    this.prefix = prefix;
  }

  // Method to extract caller information from the stack trace
  private getCaller(): string | null {
    try {
      const error = new Error();
      const stackLines = error.stack?.split('\n');
      // Extract the line representing the caller (usually 3rd line in the stack)
      return stackLines && stackLines.length > 3 ? stackLines[3].trim() : null;
    } catch {
      return null;
    }
  }
}
